'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { VerifyEmailResult } from '@/app/actions/auth'

type CodeVerificationMode = 'verifyEmail' | 'magicLinkLogin'

interface CodeVerificationFormProps {
  mode: CodeVerificationMode
  /** Required when asFormContents is false (default). Omit when asFormContents is true (parent provides <form>). */
  formAction?: React.ComponentProps<'form'>['action']
  isPending: boolean
  codeFormError: string | null | undefined
  email?: string
  onResendCode: () => void
  isResendLoading: boolean
  resendState: VerifyEmailResult | null
  /** When true, render only form contents; parent must wrap in <form action={...}> so useActionState updates. */
  asFormContents?: boolean
}

export function CodeVerificationForm({
  mode,
  formAction,
  isPending,
  codeFormError,
  email = '',
  onResendCode,
  isResendLoading,
  resendState,
  asFormContents = false,
}: CodeVerificationFormProps) {
  const codeLabel = mode === 'verifyEmail' ? 'Verification code' : 'Sign-in code'
  const submitLabel =
    mode === 'verifyEmail'
      ? isPending
        ? 'Verifying...'
        : 'Verify email'
      : isPending
        ? 'Signing in...'
        : 'Sign in'

  const formContent = (
    <>
      {mode === 'magicLinkLogin' && <input type="hidden" name="email" value={email} readOnly />}
      <div className="space-y-2">
        <Label htmlFor="code">{codeLabel}</Label>
        <Input
          id="code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          maxLength={6}
          className="font-mono text-lg tracking-widest"
          required
          aria-invalid={!!codeFormError}
          aria-describedby={codeFormError ? 'code-error' : undefined}
        />
        {codeFormError && (
          <p id="code-error" className="text-sm text-destructive" role="alert">
            {codeFormError}
          </p>
        )}
      </div>

      <Button type="submit" variant="accent" className="w-full" disabled={isPending}>
        {submitLabel}
      </Button>
    </>
  )

  return (
    <div className="mx-auto flex max-w-md flex-col justify-center p-4">
      {asFormContents ? (
        <div className="space-y-4">{formContent}</div>
      ) : (
        <form action={formAction} className="space-y-4">
          {formContent}
        </form>
      )}

      <div className="mt-6 border-t border-border pt-4">
        <p className="text-sm text-muted-foreground">Didn&apos;t receive the code?</p>
        <Button
          type="button"
          variant="link"
          onClick={onResendCode}
          disabled={isResendLoading}
          className="mt-1 p-0 text-sm"
        >
          {isResendLoading ? 'Sending…' : 'Send a new code'}
        </Button>
        {resendState?.error && <p className="mt-1 text-sm text-destructive">{resendState.error}</p>}
        {resendState?.success && (
          <p className="mt-1 text-sm text-green-600">New code sent. Check your email.</p>
        )}
      </div>
    </div>
  )
}
